import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// EditorLevelsPage.tsx — the authored half: an environment's templates. Click one to open it in the
// editor; select several (or all) and Compile & Publish them one by one with progress.
import { Component } from "react";
import { deleteEditorLevel, getEditorLevelContent, listGameLevels, listEditorLevels, promoteLevels, publishLevel, renameEditorLevel, } from "../ContentApi";
import { targetStore, PRODUCTION_TARGET_ID } from "../targetStore";
import { downloadText, levelFileName } from "../download";
export class EditorLevelsPage extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            levels: [],
            loading: true,
            error: null,
            environmentFound: true,
            openingId: null,
            openError: null,
            downloadingId: null,
            targetId: targetStore.id,
            selectedIds: new Set(),
            publishing: false,
            publishProgress: null,
            publishResult: null,
            promoting: false,
            promoteResult: null,
        };
        this.unsubscribeTarget = null;
        // Incremented by every loadLevels() call; a response whose token is no longer the latest is dropped.
        // A plain "is this still the selected target?" check isn't enough: two loads for the SAME target can
        // overlap (switch away and back, or a refresh landing on top of a switch) and the older one resolving
        // last would overwrite the list with its stale snapshot.
        this.loadToken = 0;
        this.loadLevels = async () => {
            const token = ++this.loadToken;
            this.setState({ loading: true, error: null });
            try {
                const res = await listEditorLevels(targetStore.id);
                if (token !== this.loadToken) {
                    return;
                }
                this.setState({
                    levels: res.levels ?? [],
                    environmentFound: res.environmentFound !== false,
                    error: res.error ?? null,
                    loading: false,
                    selectedIds: new Set(), // selection is stale after a reload
                });
            }
            catch (e) {
                if (token !== this.loadToken) {
                    return;
                }
                this.setState({ error: e instanceof Error ? e.message : "Failed to list templates.", loading: false });
            }
        };
        this.onOpen = async (level) => {
            this.setState({ openingId: level.id, openError: null });
            try {
                const text = await getEditorLevelContent(level.id);
                const template = JSON.parse(text);
                this.props.onOpen(template);
                this.setState({ openingId: null });
            }
            catch (e) {
                this.setState({ openError: e instanceof Error ? e.message : "Failed to open template.", openingId: null });
            }
        };
        // Save the template to disk. The content comes from the SERVER rather than from anything already
        // on screen: the list rows carry only id and name, and a row can be stale by the time it is
        // clicked. Fetching means the file is what the store holds right now, not what it held on load.
        this.onDownload = async (level) => {
            this.setState({ downloadingId: level.id, openError: null });
            try {
                const text = await getEditorLevelContent(level.id);
                downloadText(levelFileName(targetStore.id, "template", level.name), text);
            }
            catch (e) {
                this.setState({ openError: e instanceof Error ? e.message : "Download failed." });
            }
            finally {
                this.setState({ downloadingId: null });
            }
        };
        this.onDelete = async (level) => {
            // Delete is the most destructive action on this page (it takes the compiled level with it) — with
            // no target selected the list on screen is production (see the "Showing Production" banner below),
            // so this can never fall through to deleting a production template just because nothing was picked.
            const target = targetStore.id;
            if (target === null) {
                targetStore.requestAttention();
                this.setState({ openError: "Pick a target first — delete needs to know which environment." });
                return;
            }
            const confirmed = window.confirm(`Delete "${level.name}"?\n\n` +
                `This removes the template and its compiled level together (one row holds both). ` +
                `Drive keeps deleted items in its trash, so this can be recovered if needed, but not from here.`);
            if (!confirmed) {
                return;
            }
            try {
                await deleteEditorLevel(level.id, target);
            }
            catch (e) {
                this.setState({ openError: e instanceof Error ? e.message : "Failed to delete." });
            }
            finally {
                // Reload on the error path too. A partial-success 502 ("template deleted, but the compiled
                // level could not be") means the row on screen is already gone from Drive — leaving it there
                // invites a retry that answers "Template not found". loadLevels() writes `error`, never
                // `openError`, so the message set above survives the refresh.
                await this.loadLevels();
            }
        };
        this.onRename = async (level) => {
            const target = targetStore.id;
            if (target === null) {
                targetStore.requestAttention();
                this.setState({ openError: "Pick a target first — rename needs to know which environment." });
                return;
            }
            const current = level.name.replace(/\.json$/i, "");
            const next = window.prompt("Rename template to:", current);
            if (!next || next.trim() === "" || next === current) {
                return;
            }
            try {
                await renameEditorLevel(level.id, next.trim(), target);
            }
            catch (e) {
                this.setState({ openError: e instanceof Error ? e.message : "Failed to rename." });
            }
            finally {
                // Same reason as delete above: the rename's partial-success 502 leaves the template already
                // moved, so the list has to be re-read or it keeps showing the old name.
                await this.loadLevels();
            }
        };
        this.toggleSelect = (id) => {
            this.setState((s) => {
                const next = new Set(s.selectedIds);
                if (next.has(id)) {
                    next.delete(id);
                }
                else {
                    next.add(id);
                }
                return { selectedIds: next };
            });
        };
        this.toggleSelectAll = () => {
            this.setState((s) => {
                const allSelected = s.levels.length > 0 && s.levels.every((l) => s.selectedIds.has(l.id));
                return { selectedIds: allSelected ? new Set() : new Set(s.levels.map((l) => l.id)) };
            });
        };
        // Promotion only exists for dev environments — production is already the destination.
        this.onPromote = async () => {
            const target = targetStore.id;
            if (target === null || target === PRODUCTION_TARGET_ID) {
                return;
            }
            const names = this.state.levels
                .filter((l) => this.state.selectedIds.has(l.id))
                .map((l) => l.name);
            if (names.length === 0) {
                this.setState({ openError: "Select at least one level to promote." });
                return;
            }
            // Say what is about to be overwritten instead of finding out afterwards. Production already
            // carries an untitled.json that nobody meant to put there.
            //
            // The collision set is the union of BOTH listings. That mattered when the halves were separate
            // Drive files that could disagree — checking templates alone missed a level whose template was
            // absent from production but whose compiled half was already live, so the preview promised
            // nothing would be overwritten and then clobbered a level players were playing. One row holds
            // both halves now, so the two listings can no longer disagree; the union is kept because it is
            // still the correct question and costs one request.
            let collisions;
            try {
                const [prodEditorRes, prodGameRes] = await Promise.all([
                    listEditorLevels(PRODUCTION_TARGET_ID),
                    listGameLevels(PRODUCTION_TARGET_ID),
                ]);
                // A listing can come back as a 200 carrying `error` rather than throwing — treating an
                // empty list from THAT as "nothing collides" would be exactly the false negative this
                // preview exists to prevent. Fall back to the generic warning rather than asserting a
                // collision-free promote we cannot vouch for, if EITHER lookup could not be trusted.
                const editorOk = !prodEditorRes.error && prodEditorRes.environmentFound !== false;
                const gameOk = !prodGameRes.error && prodGameRes.environmentFound !== false;
                if (!editorOk || !gameOk) {
                    collisions = null;
                }
                else {
                    const existingNames = new Set([
                        ...(prodEditorRes.levels ?? []).map((l) => l.name),
                        ...(prodGameRes.levels ?? []).map((l) => l.name),
                    ]);
                    collisions = names.filter((n) => existingNames.has(n));
                }
            }
            catch {
                collisions = null;
            }
            if (!window.confirm(EditorLevelsPage.buildPromoteConfirmMessage(target, names.length, collisions))) {
                return;
            }
            // Send the overwrite permission the user actually gave. When the preview found collisions (or
            // couldn't be trusted), they confirmed a promote that replaces live levels, so the server may do
            // that. When it found none, they confirmed on the promise that nothing would be replaced — leaving
            // this false hands that promise to the server, which then skips anything that appeared in
            // production between the preview and the write instead of silently clobbering it.
            const overwriteExisting = collisions === null || collisions.length > 0;
            this.setState({ promoting: true, promoteResult: null, openError: null });
            try {
                const result = await promoteLevels(target, names, overwriteExisting);
                this.setState({ promoting: false, promoteResult: result });
            }
            catch (e) {
                this.setState({ promoting: false, openError: e instanceof Error ? e.message : "Promote failed." });
            }
        };
        // Compile + publish each selected template, one at a time, with progress. Publishing recompiles the
        // template and stores the compiled level (it appears under Game Levels), re-saving the template too.
        this.onPublishSelected = async () => {
            const targets = this.state.levels.filter((l) => this.state.selectedIds.has(l.id));
            if (targets.length === 0 || this.state.publishing) {
                return;
            }
            const target = targetStore.id;
            if (target === null) {
                targetStore.requestAttention();
                this.setState({ openError: "Pick a target first — nothing was published." });
                return;
            }
            this.setState({ publishing: true, publishResult: null, openError: null });
            const failed = [];
            let ok = 0;
            for (let i = 0; i < targets.length; i++) {
                const lvl = targets[i];
                this.setState({ publishProgress: { done: i, total: targets.length, current: lvl.name } });
                try {
                    const text = await getEditorLevelContent(lvl.id);
                    const template = JSON.parse(text);
                    await publishLevel(template, target);
                    ok++;
                }
                catch (e) {
                    failed.push(`${lvl.name}: ${e instanceof Error ? e.message : "failed"}`);
                }
            }
            this.setState({ publishing: false, publishProgress: null, publishResult: { ok, failed } });
        };
    }
    componentDidMount() {
        this.unsubscribeTarget = targetStore.subscribe(() => {
            // Everything on screen belonged to the previous target: the rows, the selection made on those
            // rows, and any result/error banner describing them. The selection matters most — Compile &
            // Publish reads the CURRENT target but the ALREADY-LOADED rows, so a selection surviving the
            // switch is a bulk write of the old target's templates into the new one (production
            // included), with no promote confirmation in the way.
            this.setState({
                targetId: targetStore.id,
                levels: [],
                selectedIds: new Set(),
                promoteResult: null,
                publishResult: null,
                publishProgress: null,
                openError: null,
            });
            void this.loadLevels();
        });
        void this.loadLevels();
    }
    componentWillUnmount() {
        this.unsubscribeTarget?.();
    }
    // Builds the promote confirmation text. `collisions` is the list of selected names that already exist
    // in production, or `null` when that couldn't be determined (the lookup failed) — in which case this
    // falls back to the old generic warning rather than claiming (possibly wrongly) that nothing collides.
    static buildPromoteConfirmMessage(target, total, collisions) {
        const base = `Promote ${total} level(s) from ${target} to DEFAULT (what the game ships from)?`;
        if (collisions === null) {
            return `${base}\n\nExisting Default files with the same names will be overwritten.`;
        }
        if (collisions.length === 0) {
            return `${base}\n\nNo existing Default files will be overwritten.`;
        }
        const named = collisions.slice(0, EditorLevelsPage.MAX_NAMED_COLLISIONS);
        const remainder = collisions.length - named.length;
        const list = remainder > 0 ? `${named.join(", ")}, and ${remainder} more` : named.join(", ");
        const noun = collisions.length === 1 ? "file" : "files";
        return `${base}\n\n${collisions.length} of these will overwrite an existing Default ${noun}: ${list}.`;
    }
    render() {
        const { levels, loading, error, environmentFound, openingId, openError, downloadingId, targetId, selectedIds, publishing, publishProgress, publishResult, promoting, promoteResult, } = this.state;
        const allSelected = levels.length > 0 && levels.every((l) => selectedIds.has(l.id));
        // `loading` belongs in here: while a reload is in flight the rows and the selection on screen are
        // the PREVIOUS target's, so every action below (publish, promote, delete, rename, open) would act
        // on a list that is about to be replaced.
        const busy = loading || publishing || openingId !== null || promoting;
        return (_jsxs("div", { className: "flex h-full flex-col", children: [targetId === null ? (_jsx("p", { className: "mb-2 px-4 text-xs text-amber-400", children: "\u26A0 Showing Default (read-only view)" })) : (_jsx("p", { className: "mb-2 px-4 text-xs text-slate-500", children: targetId })), _jsxs("div", { className: "flex items-center justify-between border-b border-slate-800 px-6 py-3", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-sm font-semibold text-slate-200", children: "Editor Levels" }), _jsx("p", { className: "text-xs text-slate-500", children: "Authored templates in this environment" })] }), _jsx("button", { type: "button", onClick: this.loadLevels, disabled: busy, className: "rounded-md border border-slate-600 px-2.5 py-1 text-xs text-slate-300 transition hover:bg-slate-700 disabled:opacity-50", children: loading ? "Loading…" : "Refresh" })] }), environmentFound && levels.length > 0 && (_jsxs("div", { className: "flex items-center gap-3 border-b border-slate-800 bg-slate-900/60 px-4 py-2", children: [_jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-300", children: [_jsx("input", { type: "checkbox", checked: allSelected, onChange: this.toggleSelectAll, disabled: busy }), "Select all"] }), _jsxs("span", { className: "text-xs text-slate-500", children: [selectedIds.size, " selected"] }), _jsx("button", { type: "button", onClick: this.onPublishSelected, disabled: selectedIds.size === 0 || busy, className: "ml-auto rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-indigo-500 disabled:opacity-50", children: publishing ? "Publishing…" : `Compile & Publish (${selectedIds.size})` }), targetId !== null && targetId !== PRODUCTION_TARGET_ID && (_jsx("button", { type: "button", onClick: this.onPromote, disabled: selectedIds.size === 0 || busy, className: "rounded-md bg-emerald-700 px-3 py-1 text-xs text-emerald-50 hover:bg-emerald-600 disabled:opacity-50", children: promoting ? "Promoting…" : `Send ${selectedIds.size} to Default` }))] })), (publishProgress || publishResult) && (_jsxs("div", { className: "border-b border-slate-800 px-4 py-2", children: [publishProgress && (_jsxs("div", { children: [_jsxs("div", { className: "mb-1 flex justify-between text-[11px] text-slate-400", children: [_jsxs("span", { children: ["Publishing ", publishProgress.done + 1, "/", publishProgress.total, ": ", _jsx("span", { className: "font-mono text-slate-300", children: publishProgress.current })] }), _jsxs("span", { children: [Math.round((publishProgress.done / publishProgress.total) * 100), "%"] })] }), _jsx("div", { className: "h-1.5 w-full overflow-hidden rounded bg-slate-800", children: _jsx("div", { className: "h-full bg-indigo-500 transition-[width]", style: { width: `${(publishProgress.done / publishProgress.total) * 100}%` } }) })] })), publishResult && (_jsxs("div", { className: "text-xs", children: [_jsxs("span", { className: "text-emerald-400", children: ["Published ", publishResult.ok] }), publishResult.failed.length > 0 && _jsxs("span", { className: "text-red-400", children: [" \u00B7 ", publishResult.failed.length, " failed"] }), publishResult.failed.length > 0 && (_jsx("ul", { className: "mt-1 space-y-0.5 text-[11px] text-red-300", children: publishResult.failed.map((f, i) => (_jsx("li", { className: "font-mono", children: f }, i))) }))] }))] })), promoteResult && (_jsxs("div", { className: "mx-4 mb-2 rounded-md border border-slate-700 bg-slate-800 p-2 text-xs", children: [_jsxs("p", { className: "text-emerald-400", children: [promoteResult.promoted.length, " promoted"] }), promoteResult.overwritten.length > 0 && (
                        // Which live levels were replaced is the one thing promote cannot undo, so it
                        // gets its own line rather than being folded into the count above.
                        _jsxs("p", { className: "text-amber-400", children: [promoteResult.overwritten.length, " replaced a level that was already live:", " ", promoteResult.overwritten.join(", ")] })), promoteResult.skipped.map((s) => (_jsxs("p", { className: "text-amber-400", children: [s.name, " skipped \u2014 ", s.reason] }, s.name))), promoteResult.failed.map((f) => (_jsxs("p", { className: "text-red-400", children: [f.name, " failed \u2014 ", f.error] }, f.name)))] })), _jsxs("div", { className: "flex-1 overflow-auto px-4 py-3", children: [!environmentFound && (_jsx("div", { className: "rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300", children: "That environment no longer exists \u2014 pick another one on the left." })), error && (_jsx("div", { className: "rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300", children: error })), openError && (_jsx("div", { className: "mb-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300", children: openError })), environmentFound && !error && (_jsxs("ul", { className: "space-y-1", children: [levels.map((lvl) => {
                                    const isSelected = selectedIds.has(lvl.id);
                                    const isPublishingThis = publishProgress?.current === lvl.name;
                                    return (_jsxs("li", { className: "flex items-center gap-1", children: [_jsx("input", { type: "checkbox", checked: isSelected, onChange: () => this.toggleSelect(lvl.id), disabled: busy, className: "ml-1 mr-1" }), _jsxs("button", { type: "button", onClick: () => this.onOpen(lvl), disabled: busy, className: "flex flex-1 items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition disabled:opacity-50 " +
                                                    (isPublishingThis ? "border-indigo-500 bg-indigo-600/20 text-white" : "border-slate-700/60 bg-slate-800/40 text-slate-300 hover:bg-slate-800"), children: [_jsx("svg", { className: "h-4 w-4 shrink-0 text-slate-500", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.8", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M4 5h6l2 2h8v11H4z" }) }), _jsx("span", { className: "font-mono", children: lvl.name }), openingId === lvl.id && _jsx("span", { className: "ml-auto text-xs text-slate-500", children: "Opening\u2026" }), isPublishingThis && _jsx("span", { className: "ml-auto text-xs text-indigo-300", children: "Publishing\u2026" })] }), _jsx("button", { type: "button", onClick: () => this.onDownload(lvl), disabled: busy || downloadingId !== null, title: "Download template JSON", className: "rounded-md border border-slate-700 px-2 py-2 text-xs text-slate-400 transition hover:bg-slate-700 hover:text-slate-200 disabled:opacity-50", children: downloadingId === lvl.id ? "…" : "⤓" }), _jsx("button", { type: "button", onClick: () => this.onRename(lvl), disabled: busy, title: "Rename", className: "rounded-md border border-slate-700 px-2 py-2 text-xs text-slate-400 transition hover:bg-slate-700 hover:text-slate-200 disabled:opacity-50", children: "\u270E" }), _jsx("button", { type: "button", onClick: () => this.onDelete(lvl), disabled: busy, title: "Delete", className: "rounded-md border border-slate-700 px-2 py-2 text-xs text-slate-400 transition hover:bg-red-600/70 hover:text-white disabled:opacity-50", children: "\uD83D\uDDD1" })] }, lvl.id));
                                }), !loading && levels.length === 0 && (_jsx("li", { className: "px-1 py-4 text-center text-sm text-slate-500", children: "No templates yet." }))] }))] })] }));
    }
}
// How many collision names to spell out in the promote confirm before falling back to "and N more" —
// naming every file is fine up to a handful, unreadable well past it.
EditorLevelsPage.MAX_NAMED_COLLISIONS = 5;
